import { NgClass, NgIf, NgStyle } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { VdoAutoTestIdDirective, VdoIconComponent } from 'ui-kit';
import { VdoMenuActionEnum } from './enum/vdo-menu-action.enum';
import { IVdoMenuItem } from './interfaces/vdo-menu-item.interface';
import { VdoSidebarService } from './services/vdo-sidebar.service';
import { hasAnyFeature } from '@core/utils/has-feature.util';
import { UserSessionStorageService } from '@core/services/user-session-storage.service';

@Component({
  selector: 'vdo-sidebar',
  imports: [
    VdoIconComponent,
    NgClass,
    MatRipple,
    RouterLink,
    RouterLinkActive,
    NgIf,
    NgStyle,
    TranslatePipe,
    VdoAutoTestIdDirective,
    MatTooltip,
  ],
  templateUrl: './vdo-sidebar.component.html',
  styleUrl: './vdo-sidebar.component.scss',
})
export class VdoSidebarComponent implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();
  @Input() @HostBinding('class.--compact') compact = true;

  private readonly session = inject(UserSessionStorageService);

  private readonly VdoSidebarService = inject(VdoSidebarService);

  menuItems = computed(() => {
    const PUBLIC_SETTINGS_MENU_ID = 'settings';
    const features = this.session?.getFeature() ?? [];
    return this.VdoSidebarService.menuItems()
      .filter(
        item => hasAnyFeature(features, item.permission) || item.id === PUBLIC_SETTINGS_MENU_ID
      )
      .map(item => {
        const filteredChildren = item.children?.filter(child =>
          hasAnyFeature(features, child.permission)
        );
        return {
          ...item,
          children: filteredChildren?.length ? filteredChildren.map(child => ({ ...child })) : [],
        } as IVdoMenuItem;
      });
  });

  isMenuOpen: boolean = true;

  private readonly router = inject(Router);

  ngOnInit(): void {
    this.highlightActiveRoute(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.highlightActiveRoute(event.url);
      });
  }

  menuOpen() {
    this.toggleSidebar.emit();
    this.isMenuOpen = !this.isMenuOpen;
  }

  menuItemAction(menuItem: IVdoMenuItem): void {
    if (menuItem.action === VdoMenuActionEnum.LOGOUT) return;
    if (!menuItem.children?.length) return;
    this.submenuOpen(menuItem);
  }

  private submenuOpen(menuItem: IVdoMenuItem): void {
    menuItem.expanded = !menuItem.expanded;
  }

  private highlightActiveRoute(url: string): void {
    for (const menuItem of this.menuItems()) {
      menuItem.isActive = false;

      if (menuItem.children?.length) {
        for (const child of menuItem.children) {
          child.isActive = false;
        }

        const activeChild = menuItem.children
          .filter(child => this.isMenuLinkActive(url, child.link))
          .sort((currentChild, nextChild) => nextChild.link.length - currentChild.link.length)[0];

        if (activeChild) {
          menuItem.expanded = true;
          activeChild.isActive = true;
          menuItem.isActive = true;
        }
      } else if (menuItem.link && url.startsWith(menuItem.link) && menuItem.link !== '/') {
        menuItem.isActive = true;
      }
    }
  }

  private isMenuLinkActive(url: string, link: string): boolean {
    if (!link || link === '/') return false;

    return url === link || url.startsWith(`${link}/`);
  }
}
